import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  User, 
  Building2, 
  FileText, 
  Calendar, 
  CheckCircle2, 
  Clock,
  ChevronRight
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfilePage = () => {
  const { user, isPresident, isAdmin } = useAuth();
  const [association, setAssociation] = useState(null);
  const [mySignature, setMySignature] = useState(null);
  const [myActivities, setMyActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const promises = [];
        
        if (user?.association_id) {
          promises.push(axios.get(`${API_URL}/associations/${user.association_id}`));
          promises.push(axios.get(`${API_URL}/my-signature`));
          promises.push(axios.get(`${API_URL}/activities/my`));
        }
        
        const results = await Promise.all(promises);
        
        if (results[0]) setAssociation(results[0].data);
        if (results[1]) setMySignature(results[1].data);
        if (results[2]) setMyActivities(results[2].data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    
    if (user) fetchData();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Header */}
        <Card className="mb-8">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cameroon-green via-cameroon-red to-cameroon-yellow flex items-center justify-center">
                <span className="text-white font-serif font-bold text-3xl">
                  {user?.full_name?.charAt(0).toUpperCase()}
                </span>
              </div>
              
              <div className="flex-1">
                <h1 className="font-serif text-2xl sm:text-3xl font-bold">{user?.full_name}</h1>
                <p className="text-muted-foreground">{user?.email}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Badge className={isAdmin() ? 'bg-cameroon-red text-white' : 'bg-cameroon-green text-white'}>
                    {user?.role === 'admin' ? 'Administrator' : 'Association President'}
                  </Badge>
                  {association && (
                    <Badge variant="outline">{association.short_name}</Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Association Info */}
          {association && (
            <Card>
              <CardHeader>
                <CardTitle className="font-serif flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-cameroon-green" />
                  My Association
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold">{association.name}</h3>
                  <p className="text-sm text-muted-foreground">{association.region} Region</p>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-3">
                  {association.description}
                </p>
                <Link to={`/associations/${association.id}`}>
                  <Button variant="outline" className="w-full" data-testid="view-association-btn">
                    View Association Page
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
          
          {/* Constitution Status */}
          {isPresident() && (
            <Card>
              <CardHeader>
                <CardTitle className="font-serif flex items-center gap-2">
                  <FileText className="w-5 h-5 text-cameroon-red" />
                  Constitution Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                {mySignature ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-cameroon-green/10 rounded-lg border border-cameroon-green/20">
                      <div className="flex items-center gap-2 text-cameroon-green mb-2">
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="font-medium">Constitution Signed</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Signed on: {new Date(mySignature.signed_at).toLocaleDateString()}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Version: {mySignature.constitution_version}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-cameroon-yellow/10 rounded-lg border border-cameroon-yellow/20">
                      <div className="flex items-center gap-2 text-cameroon-yellow mb-2">
                        <Clock className="w-5 h-5" />
                        <span className="font-medium">Signature Pending</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        You haven't signed the CCA constitution yet.
                      </p>
                    </div>
                    <Link to="/constitution">
                      <Button className="w-full bg-cameroon-green hover:bg-cameroon-green/90" data-testid="sign-now-btn">
                        Sign Now
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* Activities Summary */}
          {isPresident() && (
            <Card>
              <CardHeader>
                <CardTitle className="font-serif flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-cameroon-yellow" />
                  My Activities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="text-2xl font-bold">{myActivities.length}</div>
                    <div className="text-xs text-muted-foreground">Total</div>
                  </div>
                  <div className="text-center p-3 bg-cameroon-green/10 rounded-lg">
                    <div className="text-2xl font-bold text-cameroon-green">
                      {myActivities.filter(a => a.status === 'approved').length}
                    </div>
                    <div className="text-xs text-muted-foreground">Approved</div>
                  </div>
                  <div className="text-center p-3 bg-cameroon-yellow/10 rounded-lg">
                    <div className="text-2xl font-bold text-cameroon-yellow">
                      {myActivities.filter(a => a.status === 'pending').length}
                    </div>
                    <div className="text-xs text-muted-foreground">Pending</div>
                  </div>
                </div>
                <Link to="/activities">
                  <Button variant="outline" className="w-full" data-testid="manage-activities-btn">
                    Manage Activities
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
          
          {/* Admin Quick Access */}
          {isAdmin() && (
            <Card>
              <CardHeader>
                <CardTitle className="font-serif flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  Admin Access
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  As an administrator, you have full access to manage the CCA platform.
                </p>
                <Link to="/admin">
                  <Button className="w-full bg-cameroon-green hover:bg-cameroon-green/90" data-testid="admin-dashboard-btn">
                    Go to Admin Dashboard
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default ProfilePage;
